import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, FileText, ShieldCheck, Sparkles, Wallet } from 'lucide-react';
import { SiteHeader } from '../../components/SiteHeader';
import { SiteFooter } from '../../components/SiteFooter';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { Button } from '../../components/ui/Button';
import { HankoStamp } from '../../components/ui/HankoStamp';
import { Reveal } from '../../components/landing/Reveal';
import { API_ORIGIN } from '../../lib/api';
import { useDocumentMeta } from '../../lib/useDocumentMeta';

/**
 * Interactive sandbox demo (spec §3.A). All state lives in the browser —
 * no signup, no API. Walks the viewer through the four key stages
 * PlyEZ claims as wedges: inbox AI suggestion → 稟議 with hanko →
 * Zengin payment file → cross-border with W-8 on file.
 */
type Stage = 'inbox' | 'ringi' | 'payment' | 'cross';

interface SeedInvoice {
  id: string;
  vendor: string;
  vendorKana?: string;
  registration?: string;
  totalLabel: string;
  currency: string;
  aiGl: string;
  aiTax: string;
  aiConfidence: number;
}

const SEED_INVOICES: SeedInvoice[] = [
  {
    id: 'INV-2026-04-119',
    vendor: '株式会社 サクラ広告',
    vendorKana: 'サクラコウコク',
    registration: 'T1234567890123',
    totalLabel: '¥548,000',
    currency: 'JPY',
    aiGl: '広告宣伝費',
    aiTax: '消費税 10%',
    aiConfidence: 0.97,
  },
  {
    id: 'STR-INV-9342',
    vendor: 'Stripe Payments Inc.',
    totalLabel: '$2,418.00',
    currency: 'USD',
    aiGl: 'Software & Tooling',
    aiTax: 'No JP tax — non-resident',
    aiConfidence: 0.94,
  },
  {
    id: 'AWS-04-2026',
    vendor: 'Amazon Web Services Japan G.K.',
    vendorKana: 'アマゾン ウェブ サービス',
    registration: 'T7011001067770',
    totalLabel: '¥1,248,930',
    currency: 'JPY',
    aiGl: 'クラウド利用料',
    aiTax: '消費税 10%',
    aiConfidence: 0.98,
  },
];

export function DemoPage() {
  const { t } = useTranslation();
  useDocumentMeta({
    title: 'Live demo — PlyEZ',
    description: 'No-signup PlyEZ demo: inbox AI → 稟議 with hanko → 全銀フォーマット payment file → cross-border with W-8. Runs in 30 seconds.',
    path: '/demo',
  });
  const [stage, setStage] = useState<Stage>('inbox');
  const [picked, setPicked] = useState<string | null>(SEED_INVOICES[0].id);
  const [approvedIds, setApprovedIds] = useState<string[]>([]);
  const [hankos, setHankos] = useState<string[]>([]);
  const [paid, setPaid] = useState(false);

  const current = useMemo(
    () => SEED_INVOICES.find((i) => i.id === picked) ?? SEED_INVOICES[0],
    [picked],
  );
  const isApproved = approvedIds.includes(current.id);

  function approve() {
    if (!isApproved) setApprovedIds((s) => [...s, current.id]);
    setHankos((s) => Array.from(new Set([...s, '田中', '佐藤'])));
  }

  function resetDemo() {
    setStage('inbox');
    setPicked(SEED_INVOICES[0].id);
    setApprovedIds([]);
    setHankos([]);
    setPaid(false);
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SiteHeader />
      <main data-landing className="washi flex-1 w-full">
        <div className="max-w-6xl w-full mx-auto px-6 py-12 lg:py-16">
          <Reveal as="div">
            <span className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]">
              <span className="sparkle-dot" aria-hidden />
              {t('demo.eyebrow')}
            </span>
            <h1 className="display text-3xl lg:text-4xl font-semibold tracking-tight mt-4">{t('demo.title')}</h1>
            <p className="mt-2 text-muted-foreground max-w-2xl">{t('demo.subtitle')}</p>
          </Reveal>

          <div className="mt-8 inline-flex rounded-xl border border-border p-1 bg-muted/60">
            {([
              ['inbox',   FileText,    t('demo.stages.inbox')],
              ['ringi',   ShieldCheck, t('demo.stages.ringi')],
              ['payment', Wallet,      t('demo.stages.payment')],
              ['cross',   Sparkles,    t('demo.stages.cross')],
            ] as const).map(([k, Icon, label]) => (
              <button
                key={k}
                onClick={() => setStage(k)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  stage === k
                    ? 'bg-card shadow-soft font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>

          <Reveal as="div" delay={0.05} className="mt-6 grid lg:grid-cols-[1fr_2fr] gap-5">
            <Card className="overflow-hidden">
              <CardHeader><div className="font-medium text-sm">{t('demo.inboxTitle')}</div></CardHeader>
              <ul className="divide-y divide-border">
                {SEED_INVOICES.map((inv) => (
                  <li key={inv.id}>
                    <button
                      onClick={() => setPicked(inv.id)}
                      className={`w-full text-left px-5 py-3 transition-colors hover:bg-muted/60 ${
                        picked === inv.id ? 'bg-cinnabar-500/[0.06] dark:bg-cinnabar-500/[0.12]' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{inv.vendor}</div>
                          {inv.vendorKana && <div className="text-[11px] text-muted-foreground">{inv.vendorKana}</div>}
                          <div className="text-xs text-muted-foreground mt-0.5 num">{inv.id}</div>
                        </div>
                        <div className="num text-sm font-medium shrink-0">{inv.totalLabel}</div>
                      </div>
                      {approvedIds.includes(inv.id) ? (
                        <Pill tone="success" className="mt-1.5"><Check size={10} className="-ml-0.5" /> {t('common.approved')}</Pill>
                      ) : (
                        <Pill tone="muted" className="mt-1.5">{t('common.pending')}</Pill>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </Card>

            <Card>
              {stage === 'inbox' && (
                <CardBody>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t('demo.detail.aiSuggestion')}</div>
                  <div className="font-medium mt-1">{current.vendor}</div>
                  <div className="text-xs text-muted-foreground num mt-0.5">{current.id}</div>
                  {current.registration && (
                    <div className="mt-3">
                      <Pill tone="success"><ShieldCheck size={12} /> {t('demo.detail.qualified')} {current.registration}</Pill>
                    </div>
                  )}
                  <dl className="grid grid-cols-2 gap-y-3 gap-x-4 mt-5 text-sm">
                    <Row label={t('demo.detail.aiGl')} value={current.aiGl} />
                    <Row label={t('demo.detail.aiTax')} value={current.aiTax} />
                    <Row label={t('demo.detail.aiConfidence')} value={`${Math.round(current.aiConfidence * 100)} %`} mono />
                    <Row label={t('demo.detail.amount')} value={current.totalLabel} mono />
                  </dl>
                  <div className="mt-6 flex items-center gap-2 flex-wrap">
                    <Button onClick={() => setStage('ringi')}><ChevronRight size={14} /> {t('demo.detail.ctaRingi')}</Button>
                    <span className="text-xs text-muted-foreground">{t('demo.detail.aiNote')}</span>
                  </div>
                </CardBody>
              )}

              {stage === 'ringi' && (
                <CardBody>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t('demo.detail.ringi')}</div>
                  <div className="font-medium mt-1">{t('demo.detail.ringiRoute')}</div>
                  <div className="mt-4 flex items-center gap-3 flex-wrap">
                    {['田中', '佐藤'].map((seal) => (
                      <div key={seal} className="text-center">
                        <HankoStamp text={seal} size={56} animate={hankos.includes(seal)} />
                        <div className="text-[11px] text-muted-foreground mt-1">{seal}</div>
                      </div>
                    ))}
                    <div className="text-xs text-muted-foreground ml-2">
                      {isApproved ? t('demo.detail.ringiApproved') : t('demo.detail.ringiWaiting')}
                    </div>
                  </div>
                  <div className="mt-6 flex items-center gap-2 flex-wrap">
                    <Button variant={isApproved ? 'secondary' : 'primary'} onClick={approve}>
                      {isApproved ? <Check size={14} /> : <ShieldCheck size={14} />} {isApproved ? t('demo.detail.ringiApproved') : t('demo.detail.ringiApprove')}
                    </Button>
                    <Button variant="ghost" onClick={() => setStage('payment')}><ChevronRight size={14} /> {t('demo.detail.ctaPayment')}</Button>
                  </div>
                </CardBody>
              )}

              {stage === 'payment' && (
                <CardBody>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t('demo.detail.paymentRun')}</div>
                  <div className="font-medium mt-1">{t('demo.detail.zenginTitle')}</div>
                  <pre className="mt-4 text-[11px] bg-muted/60 border border-border rounded-lg p-3 overflow-x-auto leading-relaxed num">
{`1 9100000001 PLYEZ KK                   0428 0001SAKURA AD          ...
2 0005 SAKURA AD INC          0123 SHIBUYA 1 12345678 SAKURA AD     0000548000
2 0005 AWS JAPAN GK           0009 TOKYO    1 99887766 AWS JAPAN    0001248930
8 0000000002 0000000000000000000001796930
9`}
                  </pre>
                  <div className="mt-5 flex items-center gap-2 flex-wrap">
                    <Button onClick={() => setPaid(true)}>
                      {paid ? <Check size={14} /> : <Wallet size={14} />} {paid ? t('demo.detail.paid') : t('demo.detail.cut')}
                    </Button>
                    <Button variant="ghost" onClick={() => setStage('cross')}><ChevronRight size={14} /> {t('demo.detail.ctaCross')}</Button>
                  </div>
                  {paid && <div className="mt-3 text-xs text-emerald-700">{t('demo.detail.paidNote')}</div>}
                </CardBody>
              )}

              {stage === 'cross' && (
                <CardBody>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t('demo.detail.crossBorder')}</div>
                  <div className="font-medium mt-1">Manila Devshop Inc. — ₱45,000</div>
                  <dl className="grid grid-cols-2 gap-y-3 gap-x-4 mt-4 text-sm">
                    <Row label={t('demo.detail.rail')} value="Wise" />
                    <Row label={t('demo.detail.fx')} value="JPY → PHP (mid-market)" />
                    <Row label={t('demo.detail.tax')} value="W-8BEN-E on file" />
                    <Row label={t('demo.detail.eta')} value="T+1 (Manila bank)" />
                  </dl>
                  <p className="mt-4 text-xs text-muted-foreground leading-relaxed">{t('demo.detail.crossNote')}</p>
                </CardBody>
              )}
            </Card>
          </Reveal>

          <div className="rule mt-10" />
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <Button onClick={resetDemo} variant="ghost">{t('demo.reset')}</Button>
            <a
              href={`${API_ORIGIN}/auth/google`}
              className="inline-flex items-center justify-center gap-2 rounded-lg font-medium h-10 px-6 text-sm bg-foreground text-background hover:bg-foreground/90"
            >
              {t('demo.unlockCta')}
            </a>
            <span className="text-xs text-muted-foreground">{t('demo.unlockBody')}</span>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
      <div className={mono ? 'num mt-0.5' : 'mt-0.5'}>{value}</div>
    </div>
  );
}
